import { createFileRoute } from "@tanstack/react-router";
import { useLang } from "@/hooks/use-language";
import { LegalShell } from "@/components/legal/LegalShell";

export const Route = createFileRoute("/age-rating")({
  component: AgeRatingPage,
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/age-rating" },
      { title: "Age Rating & Kids Category — Jozori | الفئة العمرية — جذوري" },
      {
        name: "description",
        content:
          "Jozori is rated 4+ / Everyone. Built for children ages 4–12 with no ads, no chat, and no third-party tracking. جذوري مصنّف +٤ للأطفال من 4 إلى 12 سنة، بدون إعلانات أو دردشة أو تتبع.",
      },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/age-rating" }],
  }),
});

function AgeRatingPage() {
  const { lang } = useLang();
  const isAr = lang === "ar";

  return (
    <LegalShell
      titleEn="Age Rating & Kids Category"
      titleAr="الفئة العمرية وقسم الأطفال"
      updatedEn="May 21, 2026"
      updatedAr="21 مايو 2026"
    >
      {isAr ? <Ar /> : <En />}
    </LegalShell>
  );
}

function En() {
  return (
    <>
      <p>
        Jozori is designed from the ground up for children ages <strong>4–12</strong>{" "}
        and their families. This page summarises our age rating across stores
        and the safeguards that justify it.
      </p>

      <h2>1. Official ratings</h2>
      <ul>
        <li><strong>Apple App Store:</strong> 4+ (Made for Kids — eligible for the Kids Category).</li>
        <li><strong>Google Play:</strong> Everyone — enrolled in the Designed for Families programme; Teacher Approved eligible.</li>
        <li><strong>IARC / PEGI / ESRB:</strong> 3+ / Everyone / E.</li>
      </ul>
      <p>
        We <strong>never</strong> ship a 9+ or higher rating. If a future feature
        risks crossing that line, it ships gated behind a parent-only area, not in
        the child experience.
      </p>

      <h2>2. What's inside</h2>
      <ul>
        <li>Original bilingual (Arabic/English) stories, quizzes, and printables.</li>
        <li>Hand-illustrated characters with warm, age-appropriate art.</li>
        <li>Optional audio narration. No user-generated content. No open chat.</li>
        <li>No depictions of violence, scary imagery, romance, gambling, alcohol, or mature themes.</li>
      </ul>

      <h2>3. What's NOT inside (and never will be)</h2>
      <ul>
        <li>❌ Third-party advertising of any kind.</li>
        <li>❌ Behavioural tracking, fingerprinting, or ad IDs.</li>
        <li>❌ Open chat, comments, or messaging with strangers.</li>
        <li>❌ In-app links to social media or external sites in the child area.</li>
        <li>❌ In-app purchases inside the child experience (all billing lives behind a parent PIN).</li>
        <li>❌ Loot boxes, gambling mechanics, or pay-to-win.</li>
      </ul>

      <h2>4. Parental controls</h2>
      <ul>
        <li>A parent or legal guardian (18+) creates the account. Children never sign up directly.</li>
        <li>Billing, subscription changes, and account deletion live behind a <strong>parent PIN</strong>.</li>
        <li>Parents can pause an account, reset progress, or delete a child profile at any time.</li>
        <li>Time-on-task and content history are visible to parents in the dashboard.</li>
      </ul>

      <h2>5. Compliance posture</h2>
      <ul>
        <li><strong>COPPA (US):</strong> verifiable parental consent at account creation; no behavioural advertising; data minimisation.</li>
        <li><strong>GDPR-K (EU/UK):</strong> child-friendly notices; lawful basis = parental consent + contract.</li>
        <li><strong>Apple Kids Category:</strong> no third-party analytics on child accounts; no outgoing links without a parent gate.</li>
        <li><strong>Google Designed for Families:</strong> certified ad SDK list = empty (we ship none).</li>
      </ul>

      <h2>6. Reporting concerns</h2>
      <p>
        If you believe any content is not age-appropriate, email{" "}
        <a href="mailto:safety@jozori.com">safety@jozori.com</a>. We review every
        report within 48 hours and remove or revise material that does not meet
        our standard.
      </p>
    </>
  );
}

function Ar() {
  return (
    <>
      <p>
        صُمّم تطبيق جذوري من البداية للأطفال من سن <strong>٤ إلى ١٢ سنة</strong>{" "}
        ولعائلاتهم. هذه الصفحة تلخّص تصنيفنا العمري في المتاجر والضمانات التي
        تبرّره.
      </p>

      <h2>١. التصنيفات الرسمية</h2>
      <ul>
        <li><strong>متجر آبل:</strong> +4 (مصمّم للأطفال — مؤهّل لقسم الأطفال).</li>
        <li><strong>متجر جوجل بلاي:</strong> الجميع — مسجّل في برنامج «مصمّم للعائلات».</li>
        <li><strong>IARC / PEGI / ESRB:</strong> +3 / الجميع / E.</li>
      </ul>
      <p>
        لن نُصدر التطبيق بتصنيف +9 أو أعلى. إذا كانت ميزة مستقبلية قد تتجاوز هذا
        الحد، فستكون خلف بوّابة الأهل فقط، وليس ضمن تجربة الطفل.
      </p>

      <h2>٢. ما يحتويه التطبيق</h2>
      <ul>
        <li>قصص واختبارات وأوراق عمل ثنائية اللغة (عربي/إنجليزي).</li>
        <li>شخصيات مرسومة يدوياً بأسلوب دافئ يناسب الأطفال.</li>
        <li>سرد صوتي اختياري. لا محتوى من المستخدمين ولا دردشة مفتوحة.</li>
        <li>لا عنف ولا مشاهد مخيفة ولا رومانسية ولا قمار ولا مواضيع للكبار.</li>
      </ul>

      <h2>٣. ما لا يحتويه التطبيق (ولن يحتويه)</h2>
      <ul>
        <li>❌ أي إعلانات من طرف ثالث.</li>
        <li>❌ تتبّع سلوكي أو بصمات أجهزة أو معرّفات إعلانية.</li>
        <li>❌ دردشة مفتوحة أو تعليقات أو رسائل مع غرباء.</li>
        <li>❌ روابط لمواقع التواصل أو مواقع خارجية داخل منطقة الطفل.</li>
        <li>❌ مشتريات داخل التطبيق ضمن تجربة الطفل (كل الدفع خلف رمز الوالد).</li>
        <li>❌ صناديق الجوائز العشوائية أو ميكانيكا الدفع للفوز.</li>
      </ul>

      <h2>٤. أدوات الرقابة الأبوية</h2>
      <ul>
        <li>الوالد أو الوصي الشرعي (+18) هو من ينشئ الحساب. الأطفال لا يسجّلون مباشرة.</li>
        <li>الدفع وتغيير الاشتراك وحذف الحساب خلف <strong>رمز الوالد</strong>.</li>
        <li>يمكن للأهل إيقاف الحساب أو إعادة ضبط التقدّم أو حذف ملف الطفل في أي وقت.</li>
        <li>الوقت المستهلك وسجل المحتوى ظاهران في لوحة الأهل.</li>
      </ul>

      <h2>٥. الالتزام التنظيمي</h2>
      <ul>
        <li><strong>COPPA (الولايات المتحدة):</strong> موافقة أبوية موثّقة عند إنشاء الحساب؛ لا إعلانات سلوكية؛ تقليل البيانات.</li>
        <li><strong>GDPR-K (الاتحاد الأوروبي/المملكة المتحدة):</strong> إشعارات ودودة للأطفال؛ الأساس القانوني = موافقة الوالد + العقد.</li>
        <li><strong>قسم أطفال آبل:</strong> لا تحليلات من طرف ثالث على حسابات الأطفال؛ لا روابط خارجية دون بوّابة أبوية.</li>
        <li><strong>برنامج «مصمّم للعائلات» في جوجل:</strong> قائمة SDK الإعلانية المعتمدة = فارغة (لا نشحن أيّاً منها).</li>
      </ul>

      <h2>٦. الإبلاغ عن مخاوف</h2>
      <p>
        إذا اعتقدت أن أي محتوى غير مناسب للعمر، راسلنا على{" "}
        <a href="mailto:safety@jozori.com">safety@jozori.com</a>. نراجع كل بلاغ
        خلال ٤٨ ساعة ونحذف أو نُعدّل ما لا يستوفي معاييرنا.
      </p>
    </>
  );
}
